import Delete from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Moving from '@mui/icons-material/Moving';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { GeofenceTag, GeofenceType } from '@treadinc/horizon-api-spec';
import { convertLength } from '@turf/helpers';
import { t } from 'i18next';
import { useState } from 'react';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import Tabs from '~components/Tabs/Tabs';
import { Site } from '~hooks/useSites/models';

import {
  DEFAULT_CIRCLE_RADIUS_M,
  defaultPolygonAround,
  GeofenceOverride,
} from './geofence-utils';

const GEOFENCE_TYPE_TABS: { value: GeofenceType; labelKey: string }[] = [
  { value: GeofenceType.POLYGON, labelKey: 'admin.job_geo_debug.geofence_type_polygon' },
  { value: GeofenceType.CIRCLE, labelKey: 'admin.job_geo_debug.geofence_type_circle' },
];

const GEOFENCE_TAG_OPTIONS = Object.values(GeofenceTag).map((tag) => ({
  value: tag,
  labelKey: `admin.job_geo_debug.geofence_tag_${tag}`,
}));

interface GeofenceAccordionItemProps {
  gf: GeofenceOverride;
  isSelected: boolean;
  isEditable: boolean;
  isFeet: boolean;
  jobSite: Site | null;
  onSelect: () => void;
  onUpdate: (patch: Partial<GeofenceOverride>) => void;
  onUpdateFn: (fn: (prev: GeofenceOverride) => GeofenceOverride) => void;
  onRemove: (() => void) | null;
  loadTriggers: () => void;
}

export function GeofenceAccordionItem({
  gf,
  isSelected,
  isEditable,
  isFeet,
  jobSite,
  onSelect,
  onUpdate,
  onUpdateFn,
  onRemove,
  loadTriggers,
}: GeofenceAccordionItemProps) {
  const [tagSelectOpen, setTagSelectOpen] = useState(false);
  const [radiusInput, setRadiusInput] = useState<string | null>(null);
  const tagValue = gf.tag ?? GeofenceTag.SITE;
  const typeSummary = gf.geofenceType;
  const radiusSummary =
    gf.geofenceType === GeofenceType.CIRCLE && gf.circleRadius
      ? isFeet
        ? `${Math.round(convertLength(gf.circleRadius, 'meters', 'feet'))}ft`
        : `${Math.round(gf.circleRadius)}m`
      : '';

  return (
    <Box
      sx={{
        border: (theme) =>
          isSelected
            ? `1px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={onSelect}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          cursor: 'pointer',
          backgroundColor: (theme) =>
            isSelected ? theme.palette.action.selected : theme.palette.background.paper,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        }}
      >
        {isSelected ? (
          <ExpandLess sx={{ fontSize: 18 }} />
        ) : (
          <ExpandMore sx={{ fontSize: 18 }} />
        )}
        <Select
          value={tagValue}
          size="small"
          variant="standard"
          disabled={!isEditable || gf.movingGeofence}
          open={tagSelectOpen}
          onOpen={() => {
            if (isSelected && !gf.movingGeofence) setTagSelectOpen(true);
          }}
          onClose={() => setTagSelectOpen(false)}
          onChange={(e) => {
            const val = e.target.value;
            onUpdate({ tag: val === GeofenceTag.SITE ? null : val });
            setTagSelectOpen(false);
            loadTriggers();
          }}
          disableUnderline
          MenuProps={{ slotProps: { paper: { sx: { ml: -2 } } } }}
          sx={{
            flex: 3,
            fontWeight: 600,
            fontSize: '0.875rem',
            '.MuiSelect-select': { py: 0, pr: '20px !important' },
            '.MuiSelect-icon': {
              display: isSelected && !gf.movingGeofence ? 'block' : 'none',
            },
          }}
        >
          {GEOFENCE_TAG_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </MenuItem>
          ))}
        </Select>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            flex: 2,
            pl: 2,
            textAlign: 'left',
          }}
        >
          {[typeSummary, radiusSummary].filter(Boolean).join(' ')}
        </Typography>
        {gf.movingGeofence ? (
          <Moving
            sx={{ fontSize: '1.36rem', color: 'text.secondary', flexShrink: 0, p: 0.25 }}
          />
        ) : (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            title={t('admin.job_geo_debug.remove_geofence')}
            aria-label={t('admin.job_geo_debug.remove_geofence')}
            sx={{
              p: 0.25,
              flexShrink: 0,
              visibility: isEditable && onRemove ? 'visible' : 'hidden',
            }}
          >
            <Delete sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
      {isSelected && (
        <Box sx={{ px: 1, py: 1 }}>
          {gf.name && gf.name !== jobSite?.name && (
            <>
              <FormFieldLabel
                label={t('admin.job_geo_debug.geofence_name_label')}
                sx={{ mb: 0.5 }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 1.5 }}>
                {gf.name}
              </Typography>
            </>
          )}
          <FormFieldLabel
            label={t('admin.job_geo_debug.geofence_type_label')}
            sx={{ mb: 0.5 }}
          />
          <Tabs
            sx={{ minWidth: 0 }}
            selected={gf.geofenceType}
            onChange={(value) => {
              if (!isEditable) return;
              const type = value as GeofenceType;
              onUpdateFn((prev) => {
                const siteCenter =
                  typeof jobSite?.lat === 'number' && typeof jobSite?.lon === 'number'
                    ? { lat: jobSite.lat, lng: jobSite.lon }
                    : null;
                // Derive center from the current type's geometry so switching stays co-located
                const polygonCenter =
                  prev.polygonCoordinates.length >= 3
                    ? {
                        lat:
                          prev.polygonCoordinates.reduce((s, c) => s + c.lat, 0) /
                          prev.polygonCoordinates.length,
                        lng:
                          prev.polygonCoordinates.reduce((s, c) => s + c.lng, 0) /
                          prev.polygonCoordinates.length,
                      }
                    : null;
                const currentCenter =
                  (prev.geofenceType === GeofenceType.CIRCLE
                    ? prev.circleCenter
                    : polygonCenter) ??
                  prev.circleCenter ??
                  polygonCenter ??
                  siteCenter;
                return {
                  ...prev,
                  geofenceType: type,
                  ...(type === GeofenceType.CIRCLE && {
                    circleCenter: currentCenter,
                    circleRadius: prev.circleRadius ?? DEFAULT_CIRCLE_RADIUS_M,
                  }),
                  ...(type === GeofenceType.POLYGON &&
                    currentCenter && {
                      polygonCoordinates: defaultPolygonAround(currentCenter),
                    }),
                };
              });
              setTimeout(loadTriggers, 0);
            }}
            tabs={GEOFENCE_TYPE_TABS.map(({ value, labelKey }) => ({
              label: t(labelKey),
              value,
              disabled:
                !isEditable || (gf.movingGeofence && value === GeofenceType.POLYGON),
            }))}
          />

          {gf.geofenceType === 'circle' && (
            <Box sx={{ mt: 1.5 }}>
              <FormFieldLabel
                label={t(
                  isFeet
                    ? 'admin.job_geo_debug.circle_radius_label'
                    : 'admin.job_geo_debug.circle_radius_label_meters',
                )}
                sx={{ mb: 0.5 }}
              />
              <TextField
                type="number"
                value={
                  radiusInput !== null
                    ? radiusInput
                    : gf.circleRadius !== null && gf.circleRadius !== undefined
                      ? isFeet
                        ? String(
                            Math.round(convertLength(gf.circleRadius, 'meters', 'feet')),
                          )
                        : String(Math.round(gf.circleRadius))
                      : ''
                }
                onChange={(e) => {
                  if (!isEditable) return;
                  setRadiusInput(e.target.value);
                }}
                onFocus={() => {
                  if (!isEditable) return;
                  const display =
                    gf.circleRadius !== null && gf.circleRadius !== undefined
                      ? isFeet
                        ? String(
                            Math.round(convertLength(gf.circleRadius, 'meters', 'feet')),
                          )
                        : String(Math.round(gf.circleRadius))
                      : '';
                  setRadiusInput(display);
                }}
                onBlur={() => {
                  if (!isEditable) return;
                  const numVal = parseInt(radiusInput ?? '', 10);
                  const metersVal =
                    Number.isNaN(numVal) || numVal < 0
                      ? null
                      : isFeet
                        ? convertLength(numVal, 'feet', 'meters')
                        : numVal;
                  onUpdate({ circleRadius: metersVal });
                  setRadiusInput(null);
                  loadTriggers();
                }}
                slotProps={{ htmlInput: { min: 0, step: 1 } }}
                size="small"
                fullWidth
                disabled={!isEditable}
                placeholder="0"
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
